const scheduleOnIdle = (cb) => (window.requestIdleCallback ? window.requestIdleCallback(cb, { timeout: 200 }) : setTimeout(cb, 50));
const hasDynamicLoaderCandidates = () => !!document.querySelector(".component-loader, .dynamicLoading, .dynamicComponent");
const FAST_POST_AUTH_LOAD_KEY = "mybAuthUser_fastPostAuthLoad";

const consumeFastPostAuthLoadHint = () => {
  try {
    const isFastPostAuthLoad = sessionStorage.getItem(FAST_POST_AUTH_LOAD_KEY) === "true";
    sessionStorage.removeItem(FAST_POST_AUTH_LOAD_KEY);
    return isFastPostAuthLoad;
  } catch {
    return false;
  }
};

// ─── Delete Unnecessary Loaders ──────────────────────────────────────────────

const deleteUnnecessaryLoaders = () => {
  if (!document.body || !hasDynamicLoaderCandidates()) {
    return;
  }

  let pendingNodes = [];
  let scheduled = false;

  const processBatch = () => {
    scheduled = false;
    const nodes = pendingNodes;
    pendingNodes = [];

    const processChunk = (startIndex) => {
      const endIndex = Math.min(startIndex + 10, nodes.length);

      for (let i = startIndex; i < endIndex; i++) {
        const node = nodes[i];
        if (node.nodeType !== Node.ELEMENT_NODE) {
          continue;
        }

        const loaders = node.matches?.(".component-loader") ? [node] : (node.querySelectorAll?.(".component-loader") ?? []);

        loaders.forEach((loader) => {
          if (!loader.closest(".dynamicComponent")) {
            loader.remove();
          }
        });
      }

      if (endIndex < nodes.length) {
        scheduleOnIdle(() => processChunk(endIndex));
      }
    };

    if (nodes.length) {
      processChunk(0);
    }
  };

  const scheduleProcess = () => {
    if (scheduled) {
      return;
    }
    scheduled = true;
    scheduleOnIdle(processBatch);
  };

  const observer = new MutationObserver((mutations) => {
    let hasRelevantMutations = false;

    for (const mutation of mutations) {
      if (mutation.type === "childList" && mutation.addedNodes?.length) {
        for (const node of mutation.addedNodes) {
          pendingNodes.push(node);
        }
        hasRelevantMutations = true;
      }
    }

    if (hasRelevantMutations) {
      scheduleProcess();
    }
  });

  try {
    observer.observe(document.body, { childList: true, subtree: true });
  } catch (err) {
    console.error("Error observing body in deleteUnnecessaryLoaders:", err);
  }

  const tryDisconnect = () => {
    if (!hasDynamicLoaderCandidates()) {
      observer.disconnect();
    }
  };

  window.addEventListener("load", () => scheduleOnIdle(tryDisconnect), { once: true });
  setTimeout(tryDisconnect, 8000);
};

// ─── Global Loader Controller ─────────────────────────────────────────────────

const initGlobalLoaderController = () => {
  const isAuthor = document.body?.classList.contains("isAuthor") || !!window.Granite?.author;
  const isFastPostAuthLoad = consumeFastPostAuthLoadHint();
  const controllerStartedAt = performance.now();
  const minShowMs = isAuthor ? 900 : isFastPostAuthLoad ? 0 : 350;
  const maxShowMs = isAuthor ? 3500 : 1000;
  const componentLoaderGraceMs = isAuthor ? 600 : 350;
  const loader = document.getElementById("global-loader");

  if (!loader) {
    return;
  }

  let done = false;
  let hideTimer = null;
  let maxTimer = null;
  let removeTimer = null;
  let onVisibilityChange = null;
  let viewportCache = null;
  let viewportCacheAt = 0;

  const cleanup = (clearRemoveTimer = true) => {
    if (hideTimer) {
      clearTimeout(hideTimer);
      hideTimer = null;
    }
    if (maxTimer) {
      clearTimeout(maxTimer);
      maxTimer = null;
    }
    if (clearRemoveTimer && removeTimer) {
      clearTimeout(removeTimer);
      removeTimer = null;
    }
    if (onVisibilityChange) {
      document.removeEventListener("visibilitychange", onVisibilityChange);
      onVisibilityChange = null;
    }
  };

  const removeLoader = () => {
    done = true;
    cleanup();
    loader.remove();
  };

  const hideLoader = () => {
    if (done) {
      return;
    }
    done = true;
    cleanup(false);

    loader.classList.add("is-hidden");
    loader.addEventListener(
      "transitionend",
      (event) => {
        if (event.target === loader) {
          removeLoader();
        }
      },
      { once: true },
    );
    removeTimer = setTimeout(removeLoader, 350);
  };

  const getViewport = () => {
    const now = performance.now();

    if (!viewportCache || now - viewportCacheAt > 100) {
      viewportCache = {
        height: window.innerHeight || document.documentElement.clientHeight,
        width: window.innerWidth || document.documentElement.clientWidth,
      };
      viewportCacheAt = now;
    }

    return viewportCache;
  };

  const hasInitialVisibleComponentLoader = () => {
    const { height: viewportHeight, width: viewportWidth } = getViewport();
    const loaders = document.querySelectorAll(".dynamicLoading .component-loader");

    for (let i = 0; i < loaders.length; i++) {
      const owner = loaders[i].closest(".dynamicLoading");
      if (owner?.classList.contains("visibleComponent")) {
        continue;
      }

      const rect = (owner || loaders[i]).getBoundingClientRect();
      if (rect.bottom > 0 && rect.top < viewportHeight && rect.right > 0 && rect.left < viewportWidth) {
        return true;
      }
    }

    return false;
  };

  const waitForInitialLoadersOrHide = (deadline) => {
    if (done || document.hidden) {
      return;
    }
    if (!hasInitialVisibleComponentLoader() || performance.now() >= deadline) {
      hideLoader();
      return;
    }

    hideTimer = setTimeout(() => {
      hideTimer = null;
      waitForInitialLoadersOrHide(deadline);
    }, 100);
  };

  const scheduleHide = () => {
    if (done) {
      return;
    }
    if (hideTimer) {
      clearTimeout(hideTimer);
      hideTimer = null;
    }
    if (document.hidden) {
      return;
    }

    const remaining = Math.max(0, minShowMs - (performance.now() - controllerStartedAt));
    hideTimer = setTimeout(() => {
      hideTimer = null;
      waitForInitialLoadersOrHide(performance.now() + componentLoaderGraceMs);
    }, remaining);
  };

  const scheduleMaxTimer = () => {
    if (done) {
      return;
    }
    if (maxTimer) {
      clearTimeout(maxTimer);
      maxTimer = null;
    }
    if (document.hidden) {
      return;
    }

    const remainingMaxTime = Math.max(0, maxShowMs - (performance.now() - controllerStartedAt));
    maxTimer = setTimeout(() => {
      maxTimer = null;
      if (!document.hidden) {
        hideLoader();
      }
    }, remainingMaxTime);
  };

  const waitForCriticalVisual = () => {
    const criticalVisual = document.querySelector(".heroslider .video-poster-overlay, .heroslider img[fetchpriority='high'], main img[fetchpriority='high']");

    if (!criticalVisual) {
      scheduleHide();
      return;
    }

    if (criticalVisual.complete) {
      if (typeof criticalVisual.decode === "function") {
        criticalVisual
          .decode()
          .catch(() => {})
          .finally(scheduleHide);
      } else {
        scheduleHide();
      }
      return;
    }

    criticalVisual.addEventListener("load", scheduleHide, { once: true });
    criticalVisual.addEventListener("error", scheduleHide, { once: true });
  };

  const handleFailsafeEnd = (event) => {
    if (event.target === loader && event.animationName === "global-loader-failsafe") {
      removeLoader();
    }
  };

  loader.addEventListener("animationend", handleFailsafeEnd);

  onVisibilityChange = () => {
    if (done || document.hidden) {
      return;
    }
    scheduleMaxTimer();
    waitForCriticalVisual();
  };

  document.addEventListener("visibilitychange", onVisibilityChange);
  scheduleMaxTimer();

  if (document.readyState === "loading") {
    document.addEventListener("DOMContentLoaded", waitForCriticalVisual, { once: true });
  } else {
    waitForCriticalVisual();
  }
};

// ─── Public API ───────────────────────────────────────────────────────────────

export const initDynamicLoadersCleanup = () => {
  deleteUnnecessaryLoaders();
};

export const initCriticalGlobalLoader = () => {
  initGlobalLoaderController();
};
